import { getPaddockQueryOptions } from "@packages/api-client/hooks/paddockController";
import {
	getPaddockSoilReferenceQueryOptions,
	getSoilAnalysisQueryOptions,
} from "@packages/api-client/hooks/soilAnalysisController";
import type {
	SoilAnalysisResults,
	SoilReferenceProfile,
} from "@packages/shared-types/grass";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Pencil, Trash2 } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDeleteSoilAnalysis } from "@/api/grass";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import { SoilAnalysisForm } from "@/components/grass/soil/SoilAnalysisForm";
import { SoilAnalysisValues } from "@/components/grass/soil/SoilAnalysisValues";
import { SoilInterpretationCard } from "@/components/grass/soil/SoilInterpretationCard";
import { SoilSourceFilePane } from "@/components/grass/soil/SoilSourceFilePane";
import { formatSampleDate } from "@/components/grass/soil/soilDisplay";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DetailSkeleton } from "@/components/ui/skeleton";
import { getDateFnsLocale } from "@/i18n/dateLocale";
import { requireReachableRoute } from "@/lib/offline/routeGuard";

type SoilAnalysisSearch = { edit?: boolean };

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/grass-production/farms/$farmId/paddocks/$paddockId/soil-analyses/$analysisId/",
)({
	// The source file is a short-lived signed link and edits are never offline.
	staticData: { offline: "online-only" },
	beforeLoad: () => requireReachableRoute("online-only"),
	// `?edit=true` opens straight into the form (the paddock card's edit entry).
	validateSearch: (search: Record<string, unknown>): SoilAnalysisSearch => ({
		edit: search.edit === true || search.edit === "true" ? true : undefined,
	}),
	component: SoilAnalysisDetailPage,
});

function SoilAnalysisDetailPage() {
	const { orgId, farmId, paddockId, analysisId } = Route.useParams();
	const { edit } = Route.useSearch();
	const { t, i18n } = useTranslation();
	const navigate = Route.useNavigate();
	const [deleteOpen, setDeleteOpen] = useState(false);
	const deleteSoilAnalysis = useDeleteSoilAnalysis();
	const { data: referenceData } = useQuery(
		getPaddockSoilReferenceQueryOptions(orgId, farmId, paddockId),
	);
	const selectedReference = referenceData?.profiles.find(
		(profile) => profile.id === referenceData.selectedProfileId,
	) as SoilReferenceProfile | undefined;

	const { data, isLoading } = useQuery(
		getSoilAnalysisQueryOptions(orgId, farmId, paddockId, analysisId),
	);
	const { data: paddock } = useQuery(
		getPaddockQueryOptions(orgId, farmId, paddockId),
	);

	const backToPaddock = () =>
		navigate({
			to: "/app/org/$orgId/grass-production/farms/$farmId/paddocks/$paddockId",
			params: { orgId, farmId, paddockId },
			hash: "soil",
		});
	const setEditing = (editing: boolean) =>
		navigate({
			search: { edit: editing ? true : undefined },
			replace: true,
		});

	if (isLoading) return <DetailSkeleton />;

	if (!data) {
		return (
			<div className="flex h-full items-center justify-center">
				<p className="text-base-content/70">
					{t("grass.soilAnalysis.details.notFound")}
				</p>
			</div>
		);
	}

	const dateLocale = getDateFnsLocale(i18n.language);
	const results = data.results as unknown as SoilAnalysisResults;
	const analysis = { ...data, results };
	const sampledLabel = data.sampledAt
		? formatSampleDate(data.sampledAt, "PP", dateLocale)
		: t("grass.soilAnalysis.interpretation.noDate");
	const dateOf = (iso: string | undefined) =>
		iso ? formatSampleDate(iso, "PP", dateLocale) : undefined;
	const depth =
		data.depthTopCm != null && data.depthBottomCm != null
			? `${data.depthTopCm}–${data.depthBottomCm} cm`
			: undefined;
	const hasSource = Boolean(data.sourceMediaFileId);

	const confirmDelete = () =>
		deleteSoilAnalysis.mutate(
			{
				organizationId: orgId,
				establishmentId: farmId,
				paddockId,
				analysisId,
			},
			{
				onSuccess: () => {
					setDeleteOpen(false);
					backToPaddock();
				},
			},
		);

	const details = edit ? (
		<SoilAnalysisForm
			mode="edit"
			initial={analysis}
			orgId={orgId}
			farmId={farmId}
			paddockId={paddockId}
			onClose={() => setEditing(false)}
		/>
	) : (
		<>
			<div className="card bg-base-100 shadow-md">
				<div className="card-body gap-4">
					<div className="flex flex-wrap items-center gap-2">
						<h2 className="font-display text-base font-semibold">
							{t("grass.soilAnalysis.details.sampleTitle")}
						</h2>
						<Badge variant="outline">
							{hasSource
								? t("grass.soilAnalysis.details.imported")
								: t("grass.soilAnalysis.details.manual")}
						</Badge>
					</div>
					<dl className="grid gap-x-8 gap-y-3 text-sm sm:grid-cols-2">
						<Fact
							label={t("grass.soilAnalysis.fields.sampleDate")}
							value={sampledLabel}
						/>
						<Fact label={t("grass.soilAnalysis.details.depth")} value={depth} />
						<Fact
							label={t("grass.soilAnalysis.fields.labName")}
							value={data.labName}
						/>
						<Fact
							label={t("grass.soilAnalysis.fields.labReportNumber")}
							value={data.labReportNumber}
						/>
						<Fact
							label={t("grass.soilAnalysis.fields.labEntrada")}
							value={dateOf(data.labEntradaAt)}
						/>
						<Fact
							label={t("grass.soilAnalysis.fields.labSaida")}
							value={dateOf(data.labSaidaAt)}
						/>
						<Fact
							label={t("grass.soilAnalysis.fields.soilTypeCode")}
							value={results.context?.soilTypeCode}
						/>
						<Fact
							label={t("grass.soilAnalysis.fields.clayActivity")}
							value={results.context?.clayActivity}
						/>
						{data.labSampleDescription && (
							<Fact
								label={t("grass.soilAnalysis.details.gleba")}
								value={
									data.sharedPaddockCount > 0
										? t("grass.soilAnalysis.glebaBadge", {
												name: data.labSampleDescription,
												count: data.sharedPaddockCount,
											})
										: data.labSampleDescription
								}
							/>
						)}
						{data.notes && (
							<Fact
								label={t("common.notes")}
								value={data.notes}
								className="sm:col-span-2"
							/>
						)}
					</dl>
				</div>
			</div>
			{selectedReference && (
				<SoilInterpretationCard
					analysis={analysis}
					reference={selectedReference}
				/>
			)}
			<SoilAnalysisValues results={results} />
		</>
	);

	return (
		// The shell is height-locked, so the page owns its scroll.
		<div className="flex h-full flex-col gap-4 overflow-y-auto pb-10">
			<PageHeader
				title={t("grass.soilAnalysis.details.title", { date: sampledLabel })}
				description={paddock?.name}
				showBackButton
				onBack={backToPaddock}
				actions={
					!edit && (
						<div className="flex items-center gap-2">
							<Button
								variant="outline"
								size="sm"
								onClick={() => setEditing(true)}
							>
								<Pencil className="mr-2 h-4 w-4" />
								{t("common.edit")}
							</Button>
							<Button
								variant="ghost"
								size="sm"
								className="text-error"
								onClick={() => setDeleteOpen(true)}
							>
								<Trash2 className="mr-2 h-4 w-4" />
								{t("common.delete")}
							</Button>
						</div>
					)
				}
			/>

			{hasSource ? (
				// Values on the left, the report they came from on the right — the
				// report stays in view while the column scrolls or is edited.
				<div className="grid items-start gap-4 lg:grid-cols-2">
					<div className="flex min-w-0 flex-col gap-4">{details}</div>
					<div className="min-w-0 lg:sticky lg:top-0 lg:h-[calc(100dvh-8rem)]">
						<SoilSourceFilePane
							orgId={orgId}
							farmId={farmId}
							paddockId={paddockId}
							analysisId={analysisId}
						/>
					</div>
				</div>
			) : (
				<div className="flex min-w-0 flex-col gap-4">{details}</div>
			)}

			<ConfirmDeleteDialog
				open={deleteOpen}
				title={t("grass.soilAnalysis.deleteTitle")}
				description={t("grass.soilAnalysis.confirmDelete")}
				onCancel={() => setDeleteOpen(false)}
				onConfirm={confirmDelete}
				isPending={deleteSoilAnalysis.isPending}
			/>
		</div>
	);
}

/** One labelled fact; "—" when the analysis does not carry it. */
function Fact({
	label,
	value,
	className,
}: {
	label: string;
	value: ReactNode;
	className?: string;
}) {
	return (
		<div className={`flex min-w-0 flex-col gap-0.5 ${className ?? ""}`}>
			<dt className="text-xs text-base-content/60">{label}</dt>
			<dd className="whitespace-pre-line break-words text-base-content">
				{value || "—"}
			</dd>
		</div>
	);
}
